import { useEffect, useMemo, useRef, useState } from "react";
import { Grid, InternalStandardProps } from "@mui/material";
import { Layout } from "../Layout";
import { styled } from "@mui/material/styles";
import { AppTypography } from "JS/React/Components/AppTypography";
import { useNavigate, useParams } from "react-router-dom";
import { useRouting } from "JS/React/Hooks/Routes";
import { AppHeader } from "JS/React/Components/AppHeader";
import {
    useEventFetcher,
    useGetBarCode,
    useGetPdfBlob,
    useGlobalEvents,
} from "JS/React/Hooks/Events";
import { config } from "JS/Config";
import moment from "moment";
import { AppButton } from "JS/React/Components/AppButton";
import { mediaService } from "JS/Services/Media";
import { UploadImageDialog } from "JS/React/Components/EventsDialog/UploadImageDialog";
import { messaging } from "JS/Helpers/UserMessaging";
import { WebCamImageDailog } from "JS/React/Components/EventsDialog/WebCamImageDailog";
import { getUserOS, isIpad } from "JS/Helpers";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import { AppDivider } from "JS/React/Components/AppDivider";
import { useThemeModeContext } from "JS/React/Context/ThemeModeProvider";
import { AppInfoEndpoints } from "../AppInfo";
import { Tabs } from "JS/Helpers/FycStatusConstants";
import { UserTabs } from "JS/React/Components/UserTabs";
import { UserImage } from "JS/React/Components/UserImage";
import { useImageUpload } from "JS/React/Hooks/Common/ImageUpload";
import { EventActions, EventCategories, EventNames } from "JS/Models";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";

const StyledRoot = styled("div")({});

const StyledBarCode = styled("img")({
    objectFit: "cover",
    width: "100%",
    height: "80px",
});

const StyledWrapperDetailContent = styled(Grid)(({ theme }) => ({
    justifyContent: "center",
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
}));

const StyledDetailsItem = styled(Grid)(({ theme }) => ({
    marginTop: theme.spacing(2),
    display: "inline-flex !important",
    alignItems: "flex-start",
}));

const StyledItemWrapper = styled("div")({
    width: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
});

const StyledDisclaimerURL = styled("span")(({ theme }) => ({
    color: `${theme.palette.primary[900]}`,
    cursor: "pointer",
}));

const StyledGreyA100Typography = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey.A100,
}));

const StyledGrey600Typography = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey[600],
}));

export interface EventsProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

const os = getUserOS();

export const EventContentDetail = (props: EventsProps) => {
    const { className, ...rest } = props;
    const params: any = useParams();
    const { eventName, selectedTab: activeTab } = params;

    const hdnFileInput = useRef(null);

    const { linkProvider } = useRouting();
    const navigate = useNavigate();
    const { homeStack } = useGlobalNavStack();
    const [pdfUrl, setPdfUrl] = useState<string>("");
    const [backgroundImageUrl, setBackgroundImageUrl] = useState<string>("");
    const [dialogs, setDialogs] = useState({
        uploadImageDialog: false,
        webCamImageDialog: false,
    });
    const [isImageExists, setIsImageExists] = useState<boolean>(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const { events: eventList, eventCalledByName } = useGlobalEvents();
    const { logFirebaseEvent } = useFirebaseLogger();

    const event = useMemo(() => {
        return eventList?.find((event) => event.EventName === eventName);
    }, [eventList, eventName]);

    const { showPrimaryUser, hasSpouse } = (event?.Tickets ?? []).reduce(
        (acc, ticket) => {
            if (ticket?.UserId === config?.user?.memberId) {
                acc.showPrimaryUser = true;
            } else {
                acc.hasSpouse = true;
            }
            return acc;
        },
        { showPrimaryUser: false, hasSpouse: false },
    );

    const [selectedTab, setSelectedTab] = useState(
        activeTab === Tabs.PRIMARY_USER
            ? event?.Tickets[0]?.UserId
                ? event?.Tickets[0]?.UserId
                : config?.user?.memberId
            : `${config?.user?.memberId}2`,
    );

    const {
        loading: loadingEvents,
        isErrorNoEvents,
        isErrorNoRegistered,
        refetch: getEventsList,
    } = useEventFetcher(eventCalledByName.isEventCalled, eventList);

    const filteredTabData = useMemo(() => {
        return event?.Tickets.filter((x) => x.UserId === selectedTab)[0];
    }, [selectedTab, event]);

    useEffect(() => {
        if (selectedTab === config?.user?.memberId) {
            navigate(
                linkProvider.react
                    .events()
                    .detail(eventName, Tabs.PRIMARY_USER),
                { replace: true },
            );
        } else {
            navigate(
                linkProvider.react
                    .events()
                    .detail(eventName, Tabs.SECONDARY_USER),
                { replace: true },
            );
        }
    }, [selectedTab, activeTab]);

    const { loading: barCodeLoading, response: barCodeUrl } = useGetBarCode(
        config?.user?.memberId,
        filteredTabData?.UserId === config?.user?.memberId ? 1 : 0,
    );

    const { refetch: getPdfBlob } = useGetPdfBlob(pdfUrl);

    const {
        uploadImageHandler,
        webCamImageHandler,
        captureUploadHandler,
        closeUploadDialogHandler,
        fetchImageUrl,
        uploadingImage,
        fetchingImage,
        imageChecked,
        imageFetchFailed,
    } = useImageUpload();

    useEffect(() => {
        logFirebaseEvent(EventNames.EVENT_TICKET_DETAIL_OPENED, {
            action: EventActions.OPEN,
            category: EventCategories.EVENTS,
            isPrimaryUser: selectedTab === config?.user?.memberId,
            eventName: event.EventName,
        });
    }, [selectedTab]);

    useEffect(() => {
        if (filteredTabData) {
            try {
                const ticketUrl = new URL(filteredTabData?.LinkToTicket);
                const bucket = ticketUrl.hostname.split(".")[0];
                const pathParts = ticketUrl.pathname.split("/").filter(Boolean);
                const filename = pathParts.pop();
                const postfix = "/" + pathParts.join("/");

                if (!bucket || !postfix || !filename) {
                    return;
                }

                mediaService
                    .getSignedUrl(bucket, postfix, filename)
                    .then((response) => setPdfUrl(response.media_url))
                    .catch((err) => console.log(err));
            } catch (err) {
                console.log("Invalid ticket URL:", err);
            }

            fetchImageUrl(filteredTabData?.UserId, setBackgroundImageUrl);
        }
    }, [filteredTabData]);

    useEffect(() => {
        // fetchImageUrl only sets a url when the api reports the image exists
        setIsImageExists(!!backgroundImageUrl);
    }, [backgroundImageUrl]);

    useEffect(() => {
        // a user who has never uploaded a photo must do so before viewing the ticket
        if (imageChecked && !backgroundImageUrl) {
            setDialogs((prev) => ({ ...prev, uploadImageDialog: true }));
        }
    }, [imageChecked, backgroundImageUrl]);

    const handleDownloadPDF = () => {
        window.open(pdfUrl, "_blank");
        getPdfBlob(pdfUrl).then((res) => {
            const fileData = new Blob([res], { type: "application/pdf" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(fileData);
            link.download = `LTD Print Event.pdf`;
            document.body.append(link);
            link.click();
            link.remove();
        });
    };

    const handleCloseUploadDialog = () => {
        // ejects only when no photo was ever uploaded; a photo missing from s3 leaves isImageExists true
        closeUploadDialogHandler(
            setDialogs,
            isImageExists,
            linkProvider.react.events().index(),
        );
    };

    const handleUploadImage = (event) => {
        uploadImageHandler(event, filteredTabData?.UserId).then((response) => {
            if (response) {
                eventCalledByName.isEventCalled
                    ? getEventsList(
                          eventCalledByName.isEventCalled,
                          eventCalledByName.eventFolderName,
                      )
                    : getEventsList("", "");
                setDialogs({ ...dialogs, uploadImageDialog: false });
            }
        });
    };

    const handleWebCamImage = () => {
        webCamImageHandler(setDialogs, dialogs);
    };

    const handleCaptureUpload = async () => {
        captureUploadHandler(
            setDialogs,
            dialogs,
            filteredTabData?.UserId,
            capturedImage,
        ).then((res) => {
            if (res) {
                setDialogs({
                    uploadImageDialog: false,
                    webCamImageDialog: false,
                });
            }
            eventCalledByName.isEventCalled
                ? getEventsList(
                      eventCalledByName.isEventCalled,
                      eventCalledByName.eventFolderName,
                  )
                : getEventsList("", "");
            setCapturedImage(null);
        });
    };

    const handleUploadClick = () => {
        hdnFileInput.current.click();
    };
    const handleTabSwitch = (userId: string) => {
        setSelectedTab(userId);
    };

    // nothing is known about the photo until the check resolves one way or the other
    const imageCheckPending = !imageChecked && !imageFetchFailed;

    const isDefaultBehaviour = isIpad ? false : os?.includes("Macintosh");

    const themeModeContext = useThemeModeContext();
    const isDark = themeModeContext.mode === "dark";

    return (
        <Layout>
            <StyledRoot {...rest} className={className}>
                <AppHeader
                    title={"My Event Registrations"}
                    canGoBack
                    searchable={false}
                    onBackClick={() => {
                        if (eventCalledByName.isEventCalled) {
                            navigate(linkProvider.react.home());
                        } else if (!homeStack?.isHomeTrack) {
                            navigate(linkProvider.react.events().index());
                        } else {
                            navigate(-1);
                        }
                    }}
                />
                {uploadingImage && (
                    <AppBackdropProgress
                        open={true}
                        backdropText={messaging?.events.uploadingPhoto}
                    />
                )}
                {!loadingEvents && (
                    <>
                        <UserTabs
                            isSecondaryActive={
                                selectedTab !== config?.user?.memberId
                            }
                            handleTabSwitch={handleTabSwitch}
                            showPrimaryUser={showPrimaryUser}
                            hasSpouse={hasSpouse}
                        />
                        <StyledWrapperDetailContent container>
                            <Grid item xs={12} md={12}>
                                {!barCodeLoading && barCodeUrl && (
                                    <>
                                        <StyledBarCode
                                            src={barCodeUrl}
                                            alt="Not-Found"
                                        />
                                        <StyledGreyA100Typography variant="body1">
                                            {filteredTabData?.UserId}
                                        </StyledGreyA100Typography>
                                    </>
                                )}
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                display="flex"
                                justifyContent="space-between"
                                marginTop="1%"
                            >
                                <StyledGreyA100Typography
                                    fontWeight={"bold"}
                                    variant="h5"
                                >
                                    {filteredTabData?.FirstName}{" "}
                                    {filteredTabData?.LastName}
                                </StyledGreyA100Typography>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                display="flex"
                                justifyContent="space-between"
                            >
                                <AppTypography
                                    fontWeight={"bold"}
                                    color={
                                        isDark
                                            ? filteredTabData?.messageColorDark
                                                ? filteredTabData?.messageColorDark
                                                : filteredTabData?.messageColoriOS
                                            : filteredTabData?.messageColoriOS
                                              ? filteredTabData?.messageColoriOS
                                              : filteredTabData?.messageColorDark
                                    }
                                    variant="h6"
                                >
                                    {filteredTabData?.message}
                                </AppTypography>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                display="flex"
                                justifyContent="space-between"
                            >
                                <AppTypography
                                    fontWeight={"bold"}
                                    color={
                                        isDark
                                            ? filteredTabData?.message2ColorDark
                                                ? filteredTabData?.message2ColorDark
                                                : filteredTabData?.message2ColoriOS
                                            : filteredTabData?.message2ColoriOS
                                              ? filteredTabData?.message2ColoriOS
                                              : filteredTabData?.message2ColorDark
                                    }
                                    variant="h6"
                                >
                                    {filteredTabData?.message2}
                                </AppTypography>
                            </Grid>
                            <Grid item xs={12}>
                                <StyledGrey600Typography variant="body1">
                                    {event?.EventName}
                                </StyledGrey600Typography>
                                <StyledGrey600Typography variant="caption">
                                    {moment(
                                        event?.EventStartDateAndTime.split(
                                            "T",
                                        )[0],
                                    ).format("LL") +
                                        " to " +
                                        moment(
                                            event?.EventEndDateAndTime.split(
                                                "T",
                                            )[0],
                                        ).format("LL")}
                                </StyledGrey600Typography>
                            </Grid>
                            <UserImage
                                isImageExists={isImageExists}
                                backgroundImageUrl={backgroundImageUrl}
                                handleUploadClick={handleUploadClick}
                                isDefaultBehaviour={isDefaultBehaviour}
                                setDialogs={setDialogs}
                                dialogs={dialogs}
                                handleUploadImage={handleUploadImage}
                                hdnFileInput={hdnFileInput}
                                fetchingImage={fetchingImage}
                                imageFetchFailed={imageFetchFailed}
                                imageCheckPending={imageCheckPending}
                            />

                            <Grid item xs={12}>
                                <AppDivider />
                            </Grid>
                            <StyledDetailsItem item xs={12}>
                                <Grid item xs={4}>
                                    <StyledGrey600Typography
                                        variant="body1"
                                        fontWeight={"bold"}
                                    >
                                        Member
                                    </StyledGrey600Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <StyledGrey600Typography variant="body1">
                                        {filteredTabData?.FirstName}{" "}
                                        {filteredTabData?.LastName}
                                    </StyledGrey600Typography>
                                    <StyledGrey600Typography variant="caption">
                                        LTD ID: {filteredTabData?.UserId}
                                    </StyledGrey600Typography>
                                </Grid>
                            </StyledDetailsItem>
                            <StyledDetailsItem item xs={12}>
                                <Grid item xs={4}>
                                    <StyledGrey600Typography
                                        variant="body1"
                                        fontWeight={"bold"}
                                    >
                                        Product
                                    </StyledGrey600Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <StyledGrey600Typography variant="body1" component="div">
                                        <StyledItemWrapper>
                                            {filteredTabData?.ProductName}
                                        </StyledItemWrapper>
                                    </StyledGrey600Typography>
                                    <StyledGrey600Typography variant="caption">
                                        SKU: {filteredTabData?.Sku}
                                    </StyledGrey600Typography>
                                </Grid>
                            </StyledDetailsItem>
                            <StyledDetailsItem item xs={12}>
                                <Grid item xs={4}>
                                    <StyledGrey600Typography fontWeight={"bold"}>
                                        Amount
                                    </StyledGrey600Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <StyledGrey600Typography variant="body1">
                                        $ {filteredTabData?.ProductPrice}.00
                                    </StyledGrey600Typography>
                                </Grid>
                            </StyledDetailsItem>
                            <StyledDetailsItem item xs={12}>
                                <Grid item xs={4}>
                                    <StyledGrey600Typography fontWeight={"bold"}>
                                        Registration
                                    </StyledGrey600Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <AppButton
                                        onClick={handleDownloadPDF}
                                        rounded
                                        buttonVariant="grey-outlined"
                                    >
                                        Download PDF
                                    </AppButton>
                                </Grid>
                            </StyledDetailsItem>
                            <Grid item xs={12} marginTop="4%" marginBottom="4%">
                                <AppDivider />
                            </Grid>
                            <Grid item xs={12}>
                                <StyledGrey600Typography variant="body1">
                                    {messaging?.events?.disclaimer}{" "}
                                    <StyledDisclaimerURL
                                        onClick={() =>
                                            window.open(
                                                config.feedbackBaseApiUrl +
                                                    AppInfoEndpoints.REGISTRATION_DISCLOURSE,
                                                "_self",
                                            )
                                        }
                                    >
                                        here
                                    </StyledDisclaimerURL>
                                    .
                                </StyledGrey600Typography>
                            </Grid>
                        </StyledWrapperDetailContent>
                    </>
                )}
                {!uploadingImage && (
                    <UploadImageDialog
                        open={dialogs?.uploadImageDialog}
                        isImageExists={isImageExists}
                        onClose={handleCloseUploadDialog}
                        onUploadImage={handleUploadImage}
                        onWebCamImage={handleWebCamImage}
                        uploadingImage={uploadingImage}
                    />
                )}
                <WebCamImageDailog
                    capturedImage={capturedImage}
                    setCapturedImage={setCapturedImage}
                    open={dialogs?.webCamImageDialog}
                    onClose={handleCloseUploadDialog}
                    onUploadCaptureImage={handleCaptureUpload}
                />

                {loadingEvents && !isErrorNoEvents && !isErrorNoRegistered && (
                    <AppBackdropProgress
                        open={true}
                        backdropText={messaging?.events?.fetchingRecords}
                    />
                )}

                {fetchingImage && (
                    <AppBackdropProgress
                        open={true}
                        backdropText={messaging?.events?.fetchingPhoto}
                    />
                )}
            </StyledRoot>
        </Layout>
    );
};
